import { useState } from 'react'
import { supabase } from '../lib/supabase'
import { Upload, Download, X, CheckCircle, AlertTriangle } from 'lucide-react'
import ExcelJS from 'exceljs'

// Column definitions — single source of truth for template + parsing
const COLUMNS = [
  { key: 'sku',                        label: 'sku *',                     required: true, width: 18,  type: 'text' },
  { key: 'name',                       label: 'name *',                    required: true, width: 32,  type: 'text' },
  { key: 'description',                label: 'description',               width: 42,  type: 'text' },
  { key: 'category',                   label: 'category',                  width: 18,  type: 'list', listKey: 'categories' },
  { key: 'product_type',               label: 'product_type',              width: 22,  type: 'list', listKey: 'product_types' },
  { key: 'unit_of_measure',            label: 'unit_of_measure',           width: 18,  type: 'list', listKey: 'units' },
  { key: 'size',                       label: 'size',                      width: 12,  type: 'text' },
  { key: 'color',                      label: 'color',                     width: 14,  type: 'text' },
  { key: 'asin',                       label: 'asin',                      width: 14,  type: 'text' },
  { key: 'upc',                        label: 'upc',                       width: 16,  type: 'text' },
  { key: 'cost_price',                 label: 'cost_price',                width: 13,  type: 'number' },
  { key: 'retail_price',               label: 'retail_price',              width: 13,  type: 'number' },
  { key: 'weight_oz',                  label: 'weight_oz',                 width: 13,  type: 'number' },
  { key: 'length_in',                  label: 'length_in',                 width: 13,  type: 'number' },
  { key: 'width_in',                   label: 'width_in',                  width: 13,  type: 'number' },
  { key: 'height_in',                  label: 'height_in',                 width: 13,  type: 'number' },
  { key: 'reorder_point',              label: 'reorder_point',             width: 15,  type: 'number' },
  { key: 'reorder_quantity',           label: 'reorder_quantity',          width: 18,  type: 'number' },
  { key: 'supplier_name',              label: 'supplier_name',             width: 24,  type: 'list', listKey: 'suppliers' },
  { key: 'shopify_enabled',            label: 'shopify_enabled',           width: 17,  type: 'list', listKey: 'bools' },
  { key: 'shopify_replen_status',      label: 'shopify_replen_status',     width: 23,  type: 'list', listKey: 'replen' },
  { key: 'amazon_enabled',             label: 'amazon_enabled',            width: 17,  type: 'list', listKey: 'bools' },
  { key: 'amazon_replen_status',       label: 'amazon_replen_status',      width: 23,  type: 'list', listKey: 'replen' },
  { key: 'sourcing_type',              label: 'sourcing_type',             width: 17,  type: 'list', listKey: 'sourcing' },
  { key: 'production_lead_time_days',  label: 'production_lead_time_days', width: 26,  type: 'number' },
  { key: 'target_days_of_supply',      label: 'target_days_of_supply',     width: 23,  type: 'number' },
  { key: 'fba_target_days',            label: 'fba_target_days',           width: 17,  type: 'number' },
  { key: 'min_shelf_life_days',        label: 'min_shelf_life_days',       width: 21,  type: 'number' },
  { key: 'is_active',                  label: 'is_active',                 width: 13,  type: 'list', listKey: 'bools' },
]

// Static dropdown values (inlined in Excel formula)
const STATIC_LISTS = {
  product_types: ['finished_good', 'component', 'raw_material', 'virtual_bundle', 'physical_bundle'],
  units:         ['each', 'case', 'dozen', 'pair', 'lb', 'oz', 'ml'],
  replen:        ['replenish', 'sunset', 'new'],
  sourcing:      ['overseas', 'domestic'],
  bools:         ['TRUE', 'FALSE'],
}

const EXAMPLE_DEFAULTS = {
  sku:                       'SKU-001',
  name:                      'Example Product — delete this row',
  description:               'Optional description',
  category:                  '',
  product_type:              'finished_good',
  unit_of_measure:           'each',
  size:                      '',
  color:                     '',
  asin:                      '',
  upc:                       '',
  cost_price:                5.00,
  retail_price:              19.99,
  weight_oz:                 '',
  length_in:                 '',
  width_in:                  '',
  height_in:                 '',
  reorder_point:             50,
  reorder_quantity:          100,
  supplier_name:             '',
  shopify_enabled:           'TRUE',
  shopify_replen_status:     'replenish',
  amazon_enabled:            'FALSE',
  amazon_replen_status:      'replenish',
  sourcing_type:             'overseas',
  production_lead_time_days: 45,
  target_days_of_supply:     90,
  fba_target_days:           60,
  min_shelf_life_days:       '',
  is_active:                 'TRUE',
}

async function generateTemplate(brandName, categories, suppliers) {
  const wb = new ExcelJS.Workbook()
  wb.creator = 'Inventory App'

  const categoryNames = (categories || []).map(c => c.name)
  const supplierNames = (suppliers || []).map(s => s.name)

  // Products sheet
  const ws = wb.addWorksheet('Products')
  ws.views = [{ state: 'frozen', ySplit: 1 }]

  ws.columns = COLUMNS.map(c => ({ key: c.key, width: c.width }))

  const headerRow = ws.addRow(COLUMNS.map(c => c.label))
  headerRow.height = 22
  headerRow.eachCell((cell, colIdx) => {
    const col = COLUMNS[colIdx - 1]
    cell.fill   = { type: 'pattern', pattern: 'solid', fgColor: { argb: col.required ? 'FFFFF2CC' : 'FFD9E1F2' } }
    cell.font   = { bold: true, size: 10, color: { argb: 'FF1F2937' } }
    cell.border = { bottom: { style: 'medium', color: { argb: 'FF9CA3AF' } } }
    cell.alignment = { vertical: 'middle' }
  })

  // Example row
  const exRow = ws.addRow(COLUMNS.map(c => EXAMPLE_DEFAULTS[c.key] ?? ''))
  exRow.eachCell(cell => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F4F6' } }
    cell.font = { italic: true, color: { argb: 'FF9CA3AF' }, size: 10 }
  })

  // Data validations starting at row 2 (includes example row) — all inline for max compatibility
  COLUMNS.forEach((col, i) => {
    if (col.type !== 'list') return
    const colLetter = ws.getColumn(i + 1).letter
    const range = `${colLetter}2:${colLetter}10001`

    let values = null
    if (col.listKey === 'categories') values = categoryNames
    else if (col.listKey === 'suppliers') values = supplierNames
    else values = STATIC_LISTS[col.listKey]

    if (!values || values.length === 0) return

    ws.dataValidations.add(range, {
      type: 'list', allowBlank: true, showErrorMessage: true,
      errorTitle: 'Invalid value', error: `Valid values: ${values.join(', ')}`,
      formulae: [`"${values.join(',')}"`],
    })
  })

  const buffer = await wb.xlsx.writeBuffer()
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `catalog_import_${(brandName || 'template').toLowerCase().replace(/\s+/g, '_')}.xlsx`
  a.click()
  URL.revokeObjectURL(url)
}

function getCellValue(cell) {
  if (cell.value === null || cell.value === undefined) return ''
  if (typeof cell.value === 'object' && 'result' in cell.value) return String(cell.value.result ?? '')
  if (typeof cell.value === 'object' && 'text' in cell.value) return String(cell.value.text ?? '')
  return String(cell.value)
}

async function parseXLSX(file) {
  const buffer = await file.arrayBuffer()
  const wb = new ExcelJS.Workbook()
  await wb.xlsx.load(buffer)
  const ws = wb.getWorksheet('Products') || wb.worksheets[0]
  if (!ws) return []

  // Row 1 = headers
  const headerRow = ws.getRow(1)
  const headers = []
  headerRow.eachCell((cell, colIdx) => {
    // Strip the ' *' required marker from header labels
    headers[colIdx] = getCellValue(cell).replace(/\s*\*$/, '').trim()
  })

  const rows = []
  ws.eachRow((row, rowNum) => {
    if (rowNum === 1) return
    const obj = {}
    let hasData = false
    row.eachCell((cell, colIdx) => {
      const key = headers[colIdx]
      if (!key) return
      const val = getCellValue(cell)
      obj[key] = val
      if (val) hasData = true
    })
    if (hasData) rows.push(obj)
  })
  return rows
}

function parseCSV(text) {
  const lines = text.trim().split('\n')
  const header = lines[0].replace(/^\uFEFF/, '').split(',').map(h => h.replace(/^"|"$/g, '').trim())
  const rows = []
  for (let i = 1; i < lines.length; i++) {
    if (!lines[i].trim()) continue
    const values = []
    let current = ''
    let inQuotes = false
    for (const char of lines[i]) {
      if (char === '"') { inQuotes = !inQuotes }
      else if (char === ',' && !inQuotes) { values.push(current.trim()); current = '' }
      else { current += char }
    }
    values.push(current.trim())
    const row = {}
    header.forEach((h, idx) => { row[h] = values[idx] || '' })
    rows.push(row)
  }
  return rows
}

function parseBool(val) {
  if (val === undefined || val === null || val === '') return null
  return val.toString().toLowerCase() === 'true' || val === '1'
}

function parseNum(val, float = false) {
  if (val === '' || val === null || val === undefined) return null
  const n = float ? parseFloat(val) : parseInt(val, 10)
  return isNaN(n) ? null : n
}

const VALID_TYPES   = ['finished_good', 'component', 'raw_material', 'virtual_bundle', 'physical_bundle']
const VALID_REPLEN  = ['replenish', 'sunset', 'new']
const VALID_SOURCING = ['overseas', 'domestic']

function validateRow(row) {
  const errors = []
  if (!row.sku)  errors.push('sku required')
  if (!row.name) errors.push('name required')
  if (row.product_type && !VALID_TYPES.includes(row.product_type))     errors.push(`invalid product_type: ${row.product_type}`)
  if (row.shopify_replen_status && !VALID_REPLEN.includes(row.shopify_replen_status))  errors.push('invalid shopify_replen_status')
  if (row.amazon_replen_status  && !VALID_REPLEN.includes(row.amazon_replen_status))   errors.push('invalid amazon_replen_status')
  if (row.sourcing_type && !VALID_SOURCING.includes(row.sourcing_type)) errors.push(`invalid sourcing_type: ${row.sourcing_type}`)
  return errors
}

export default function CatalogImport({ open, onClose, activeBrand, suppliers, categories, onDone }) {
  const [file, setFile]       = useState(null)
  const [preview, setPreview] = useState(null)
  const [importing, setImporting] = useState(false)
  const [result, setResult]   = useState(null)
  const [templateBusy, setTemplateBusy] = useState(false)

  function reset() { setFile(null); setPreview(null); setResult(null) }
  function handleClose() { reset(); onClose() }

  async function handleDownload() {
    setTemplateBusy(true)
    await generateTemplate(activeBrand?.name, categories, suppliers)
    setTemplateBusy(false)
  }

  async function handleFile(e) {
    const f = e.target.files[0]
    if (!f) return
    setFile(f)
    setPreview(null)
    setResult(null)

    const isXLSX = f.name.endsWith('.xlsx') || f.name.endsWith('.xlsm')
    let rows
    if (isXLSX) {
      rows = await parseXLSX(f)
    } else {
      rows = parseCSV(await f.text())
    }

    // Load existing SKUs
    const { data: existing } = await supabase
      .from('products').select('id, sku')
      .eq('brand_id', activeBrand.id).is('deleted_at', null)
    const existingBySku = {}
    ;(existing || []).forEach(p => { existingBySku[p.sku.toLowerCase()] = p.id })

    const supplierByName = {}
    ;(suppliers || []).forEach(s => { supplierByName[s.name.toLowerCase()] = s.id })

    const parsed = rows.map((row, idx) => {
      const errors = validateRow(row)
      const existingId = row.sku ? existingBySku[row.sku.toLowerCase()] : null
      const action = existingId ? 'update' : 'create'
      let supplier_id = null
      let supplierWarning = null
      if (row.supplier_name) {
        const matched = supplierByName[row.supplier_name.toLowerCase()]
        if (matched) { supplier_id = matched }
        else { supplierWarning = true }
      }
      return { row, action, existingId, supplier_id, supplierWarning, errors, rowNum: idx + 2 }
    })

    setPreview(parsed)
  }

  async function runImport() {
    if (!preview) return
    setImporting(true)
    let created = 0, updated = 0, failed = 0

    for (const item of preview) {
      if (item.errors.length > 0) { failed++; continue }
      const { row } = item
      const payload = {
        brand_id:                  activeBrand.id,
        sku:                       row.sku,
        name:                      row.name,
        description:               row.description || null,
        category:                  row.category || null,
        product_type:              row.product_type || 'finished_good',
        unit_of_measure:           row.unit_of_measure || 'each',
        size:                      row.size || null,
        color:                     row.color || null,
        asin:                      row.asin || null,
        upc:                       row.upc || null,
        cost_price:                parseNum(row.cost_price, true),
        retail_price:              parseNum(row.retail_price, true),
        weight_oz:                 parseNum(row.weight_oz, true),
        length_in:                 parseNum(row.length_in, true),
        width_in:                  parseNum(row.width_in, true),
        height_in:                 parseNum(row.height_in, true),
        reorder_point:             parseNum(row.reorder_point),
        reorder_quantity:          parseNum(row.reorder_quantity),
        supplier_id:               item.supplier_id,
        shopify_enabled:           row.shopify_enabled ? parseBool(row.shopify_enabled) : true,
        shopify_replen_status:     row.shopify_replen_status || 'replenish',
        amazon_enabled:            row.amazon_enabled ? parseBool(row.amazon_enabled) : false,
        amazon_replen_status:      row.amazon_replen_status || 'replenish',
        sourcing_type:             row.sourcing_type || 'overseas',
        production_lead_time_days: parseNum(row.production_lead_time_days),
        target_days_of_supply:     parseNum(row.target_days_of_supply) || 90,
        fba_target_days:           parseNum(row.fba_target_days) || 60,
        min_shelf_life_days:       parseNum(row.min_shelf_life_days),
        is_active:                 row.is_active ? parseBool(row.is_active) : true,
      }

      let error
      if (item.action === 'update' && item.existingId) {
        ;({ error } = await supabase.from('products').update(payload).eq('id', item.existingId))
      } else {
        ;({ error } = await supabase.from('products').insert(payload))
      }
      if (error) { failed++ } else if (item.action === 'create') { created++ } else { updated++ }
    }

    setResult({ created, updated, failed })
    setImporting(false)
    onDone()
  }

  if (!open) return null

  const validRows  = preview ? preview.filter(r => r.errors.length === 0) : []
  const errorRows  = preview ? preview.filter(r => r.errors.length > 0)  : []
  const creates    = validRows.filter(r => r.action === 'create').length
  const updates    = validRows.filter(r => r.action === 'update').length

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) handleClose() }}>
      <div className="modal" style={{ maxWidth: '760px', width: '95vw' }}>
        <div className="modal-header">
          <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>Import Catalog</h2>
          <button className="btn btn-ghost btn-sm" onClick={handleClose}><X size={16} /></button>
        </div>

        <div className="modal-body" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* Template download */}
          <div style={{ background: 'var(--bg-2)', borderRadius: '8px', padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '4px' }}>Step 1 — Download the template</div>
              <div style={{ fontSize: '12px', color: 'var(--text-2)', lineHeight: 1.5 }}>
                Excel file with dropdown menus for every enum field and your brand's categories and suppliers pre-loaded.
                Yellow columns are required. Delete the example row before importing.
              </div>
            </div>
            <button className="btn btn-secondary" style={{ flexShrink: 0 }} onClick={handleDownload} disabled={templateBusy}>
              <Download size={14} /> {templateBusy ? 'Building…' : 'Download'}
            </button>
          </div>

          {/* File upload */}
          {!result && (
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>
                Step 2 — Upload completed file (.xlsx or .csv)
              </label>
              <input
                type="file"
                accept=".xlsx,.xlsm,.csv"
                className="input"
                style={{ padding: '6px 8px', cursor: 'pointer' }}
                onChange={handleFile}
              />
            </div>
          )}

          {/* Preview */}
          {preview && !result && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <div style={{ background: 'var(--bg-2)', borderRadius: '6px', padding: '8px 14px', fontSize: '13px' }}>
                  <span style={{ fontWeight: 700, color: 'var(--success)' }}>{creates}</span>
                  <span style={{ color: 'var(--text-2)' }}> to create</span>
                </div>
                <div style={{ background: 'var(--bg-2)', borderRadius: '6px', padding: '8px 14px', fontSize: '13px' }}>
                  <span style={{ fontWeight: 700, color: 'var(--accent)' }}>{updates}</span>
                  <span style={{ color: 'var(--text-2)' }}> to update</span>
                </div>
                {errorRows.length > 0 && (
                  <div style={{ background: 'var(--bg-2)', borderRadius: '6px', padding: '8px 14px', fontSize: '13px' }}>
                    <span style={{ fontWeight: 700, color: 'var(--danger)' }}>{errorRows.length}</span>
                    <span style={{ color: 'var(--text-2)' }}> with errors — will be skipped</span>
                  </div>
                )}
              </div>

              <div className="table-wrap" style={{ maxHeight: '300px', overflow: 'auto', border: '1px solid var(--border)', borderRadius: '6px' }}>
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: '34px' }}>#</th>
                      <th>SKU</th>
                      <th>Name</th>
                      <th>Type</th>
                      <th>Supplier</th>
                      <th>Action</th>
                      <th>Issues</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((item, i) => (
                      <tr key={i} style={{ opacity: item.errors.length > 0 ? 0.55 : 1 }}>
                        <td style={{ fontSize: '11px', color: 'var(--text-3)' }}>{item.rowNum}</td>
                        <td><span className="mono" style={{ fontSize: '12px' }}>{item.row.sku || '—'}</span></td>
                        <td style={{ fontSize: '13px' }}>{item.row.name || '—'}</td>
                        <td style={{ fontSize: '11px', color: 'var(--text-2)' }}>{item.row.product_type || 'finished_good'}</td>
                        <td style={{ fontSize: '12px', color: item.supplierWarning ? 'var(--warning)' : 'inherit' }}>
                          {item.supplier_id ? item.row.supplier_name : (item.row.supplier_name ? '⚠ no match' : '—')}
                        </td>
                        <td>
                          <span className={`badge ${item.action === 'create' ? 'badge-success' : 'badge-accent'}`} style={{ fontSize: '10px' }}>
                            {item.action}
                          </span>
                        </td>
                        <td style={{ fontSize: '11px' }}>
                          {item.errors.length > 0
                            ? <span style={{ color: 'var(--danger)' }}>{item.errors.join(', ')}</span>
                            : item.supplierWarning
                              ? <span style={{ color: 'var(--warning)' }}>supplier skipped</span>
                              : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Result */}
          {result && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--success)', fontWeight: 600, fontSize: '14px' }}>
                <CheckCircle size={18} /> Import complete
              </div>
              <div style={{ fontSize: '13px', color: 'var(--text-2)', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div>{result.created} product{result.created !== 1 ? 's' : ''} created</div>
                <div>{result.updated} product{result.updated !== 1 ? 's' : ''} updated</div>
                {result.failed > 0 && (
                  <div style={{ color: 'var(--danger)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <AlertTriangle size={13} /> {result.failed} row{result.failed !== 1 ? 's' : ''} skipped due to errors
                  </div>
                )}
              </div>
            </div>
          )}

        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={handleClose}>{result ? 'Close' : 'Cancel'}</button>
          {preview && !result && validRows.length > 0 && (
            <button className="btn btn-primary" onClick={runImport} disabled={importing}>
              {importing ? 'Importing…' : `Import ${validRows.length} Product${validRows.length !== 1 ? 's' : ''}`}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
